import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AUTH_ME_QUERY_KEY } from '../../../lib/auth/use-app-user.js';

/** Same sign-out contract as apps/web/src/components/user-menu.tsx (storefront) -- Clerk's own signOut(), never a homegrown session clear, and the cached /auth/me query is dropped immediately so no stale admin-only state can flash. */
export function AdminUserMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user } = useUser();
  const queryClient = useQueryClient();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    queryClient.removeQueries({ queryKey: AUTH_ME_QUERY_KEY });
    navigate(ROUTE_NAMES.home, { replace: true });
  }

  const displayName = user?.firstName ?? user?.primaryEmailAddress?.emailAddress ?? 'Admin';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="hover:border-brand flex items-center gap-2 rounded-full border border-gray-200 py-1 pl-1 pr-3 text-sm font-medium text-gray-900"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        {user?.imageUrl ? (
          <img src={user.imageUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
        ) : (
          <span className="bg-brand text-brand-foreground flex h-7 w-7 items-center justify-center rounded-full">
            {displayName.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="max-w-[8rem] truncate">{displayName}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-2xl border border-gray-100 bg-white py-1 shadow-xl"
        >
          <a
            href={ROUTE_NAMES.profile}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Profile
          </a>
          <a
            href={ROUTE_NAMES.home}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Return to store
          </a>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
